import {useCallback, useSyncExternalStore} from "react";

/**
 * Переключатель уведомлений приложения (S4.3). Разрешение браузера из страницы
 * не отозвать, поэтому выключение — своё: `localStorage`, ключ на пользователя.
 * Выключено — нет системных уведомлений и звуков; всплывающие внутри приложения,
 * счётчики и мигание вкладки остаются (правила — `rules.ts`). Нет записи или
 * хранилище недоступно — включено.
 */
const keyOf = (userId: string) => `atom-eol:notify-enabled:${userId}`;

/** Событие окна: переключатель сменился в этой вкладке (у `storage` — только в других). */
const CHANGE = "atom-eol:notify-pref";

export const readNotifyEnabled = (userId: string | null): boolean => {
    if (!userId) return true;
    try {
        return localStorage.getItem(keyOf(userId)) !== "0";
    } catch {
        return true;
    }
};

export const writeNotifyEnabled = (userId: string | null, enabled: boolean): void => {
    if (!userId) return;
    try {
        localStorage.setItem(keyOf(userId), enabled ? "1" : "0");
    } catch {
        // Хранилище недоступно (приватный режим, запрет): выбор не сохранится, остаётся «включено».
    }
    window.dispatchEvent(new Event(CHANGE));
};

const subscribe = (onChange: () => void) => {
    window.addEventListener(CHANGE, onChange);
    window.addEventListener("storage", onChange);
    return () => {
        window.removeEventListener(CHANGE, onChange);
        window.removeEventListener("storage", onChange);
    };
};

/** Состояние переключателя пользователя и его смена; несколько колокольчиков видят одно значение. */
export function useNotifyEnabled(userId: string | null): [boolean, (enabled: boolean) => void] {
    const enabled = useSyncExternalStore(subscribe, () => readNotifyEnabled(userId));
    const set = useCallback((value: boolean) => writeNotifyEnabled(userId, value), [userId]);
    return [enabled, set];
}
